import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { FiltersBar, Kpi, Spark, Loading, CARD, LBL, fmtAht, fmtNum, fq, thrColor } from "./axiom";

const THR_LEVELS = [["lob", "LOB Level"], ["category", "Category Level"], ["sub_category", "Sub Category Level"], ["agent", "Agent Level"]];

export const DashboardTab = ({ summary, filters, setFilters }) => {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => { api.get("/aht/settings").then((r) => setSettings(r.data)); }, []);
  const load = useCallback(() => { api.get(`/aht/dashboard?${fq(filters)}`).then((r) => setData(r.data)); }, [filters]);
  useEffect(() => { load(); }, [load]);

  const saveThresholds = async (thr) => {
    try {
      const { data: s } = await api.put("/aht/settings", { ...settings, thresholds: thr });
      setSettings(s);
    } catch (e) { toast.error(apiError(e)); }
  };
  const setThr = (level, idx, val) => {
    const thr = { ...settings.thresholds, [level]: [...settings.thresholds[level]] };
    thr[level][idx] = Number(val) || 0;
    setSettings({ ...settings, thresholds: thr });
  };

  if (!data || !settings) return <Loading />;
  const thr = settings.thresholds;
  const maxAht = Math.max(...data.lob_comparison.map((r) => r.aht), 1);

  return (
    <div>
      <h2 className="font-heading mb-5 text-xl font-bold text-thstrong">AHT Overview Dashboard</h2>
      <FiltersBar summary={summary} filters={filters} setFilters={setFilters} />

      <div className={`${CARD} mb-6 p-5`}>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-thstrong">Conditional Formatting Thresholds</p>
            <p className="mt-0.5 text-[11px] text-thdim">Set AHT thresholds (in seconds). Applies to all tabs · ≤ Green · Green–Amber · &gt; Amber</p>
          </div>
          <button data-testid="aht-thr-reset" onClick={() => saveThresholds({ lob: [360, 420], category: [360, 420], sub_category: [360, 420], agent: [360, 420] })}
            className="rounded-lg border border-thborder/12 px-3 py-1.5 text-xs text-thmute hover:text-thcyan">↺ Reset Defaults</button>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {THR_LEVELS.map(([k, label]) => (
            <div key={k}>
              <p className="mb-2 text-xs font-semibold text-amber-600">{label}</p>
              <div className="flex items-center gap-2">
                <span className="text-xs">≤</span>
                <input data-testid={`aht-thr-${k}-green`} type="number" value={thr[k][0]}
                  onChange={(e) => setThr(k, 0, e.target.value)} onBlur={() => saveThresholds(thr)}
                  className="w-20 rounded-lg border border-thborder/12 px-2 py-1.5 text-xs" />
                <span className="text-xs">≤</span>
                <input data-testid={`aht-thr-${k}-amber`} type="number" value={thr[k][1]}
                  onChange={(e) => setThr(k, 1, e.target.value)} onBlur={() => saveThresholds(thr)}
                  className="w-20 rounded-lg border border-thborder/12 px-2 py-1.5 text-xs" />
                <span className="text-xs">&gt;</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi testId="aht-kpi-calls" label="Total Benchmark Calls" value={fmtNum(data.kpis.calls)} sub={filters.lob || "All LOBs"} accent="rgb(var(--th-strong))" />
        <Kpi testId="aht-kpi-aht" label="Overall Avg AHT" value={fmtAht(data.kpis.aht)} sub={`${Math.round(data.kpis.aht || 0)}s`} accent="rgb(var(--th-strong))" />
        <Kpi testId="aht-kpi-categories" label="Categories" value={fmtNum(data.kpis.categories)} sub={`${fmtNum(data.kpis.sub_categories)} sub-categories`} accent="rgb(var(--th-strong))" />
        <Kpi testId="aht-kpi-months" label="Months" value={fmtNum(data.kpis.months)} sub={data.kpis.month_range} accent="rgb(var(--th-strong))" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className={`${CARD} p-6`}>
          <p className="text-sm font-bold text-thstrong">LOB AHT Comparison</p>
          <p className="mb-6 text-[11px] text-thdim">Sorted worst → best · colored vs overall avg</p>
          <div className="space-y-4">
            {data.lob_comparison.map((r) => (
              <div key={r.name} className="flex items-center gap-4">
                <span className="w-32 shrink-0 text-right text-sm font-semibold text-thbody">{r.name}</span>
                <div className="relative h-6 flex-1 rounded bg-thhover/8">
                  <div className="h-6 rounded" style={{
                    width: `${(r.aht / maxAht) * 100}%`,
                    background: r.aht > (data.overall_aht || 0) ? "#F59E0B" : "#7a8fb5",
                  }} />
                </div>
                <span className="font-mono-hud w-14 text-sm font-bold text-thbody">{Math.round(r.aht)}s</span>
              </div>
            ))}
          </div>
          <p className="font-mono-hud mt-4 text-right text-[11px] text-amber-500">overall {Math.round(data.overall_aht || 0)}s</p>
        </div>

        <div className={`${CARD} p-6`}>
          <p className="text-sm font-bold text-thstrong">Current vs Benchmark AHT</p>
          <p className="mb-6 text-[11px] text-thdim">
            {settings.benchmark_month ? `Benchmark month: ${settings.benchmark_month}` : "Lock a benchmark month on the Benchmark tab"}
          </p>
          {data.target.length === 0 ? (
            <p className="py-10 text-center text-xs text-thdim">No benchmark month set — open the Benchmark tab to lock one.</p>
          ) : (
            <div className="space-y-5">
              {data.target.map((r) => (
                <div key={r.name}>
                  <div className="flex items-center gap-4">
                    <span className="w-32 shrink-0 text-right text-sm font-semibold text-thbody">{r.name}</span>
                    <div className="flex-1 space-y-1">
                      <div className="h-3.5 rounded bg-amber-500" style={{ width: `${(r.current / maxAht) * 100}%` }} />
                      <div className="h-3.5 rounded bg-red-400" style={{ width: `${((r.target || 0) / maxAht) * 100}%` }} />
                    </div>
                    <span className="font-mono-hud w-28 text-xs text-thbody">
                      {Math.round(r.current)}s / {r.target ? `${Math.round(r.target)}s` : "—"}
                    </span>
                  </div>
                </div>
              ))}
              <div className="flex gap-5 pt-2 text-[11px] text-thmute">
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-amber-500" /> Current AHT</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-red-400" /> Benchmark</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {[["By Category", data.by_category, "category"], ["By Language", data.by_lang, "lob"], ["By Location", data.by_location, "lob"]].map(([title, rows, lvl]) => (
          <div key={title} className={`${CARD} overflow-hidden`}>
            <p className="px-5 pb-2 pt-4 text-sm font-bold text-thstrong">{title}</p>
            <table className="w-full text-left">
              <thead><tr className="border-b border-thborder/8">
                <th className={`${LBL} px-5 py-2`}>Name</th><th className={`${LBL} px-5 py-2`}>Calls</th><th className={`${LBL} px-5 py-2`}>AHT</th>
              </tr></thead>
              <tbody>
                {rows.slice(0, 8).map((r) => (
                  <tr key={r.name} className="border-b border-thborder/6">
                    <td className="max-w-[160px] truncate px-5 py-2 text-xs font-medium text-thbody">{r.name}</td>
                    <td className="px-5 py-2 text-xs text-thmute">{fmtNum(r.calls)}</td>
                    <td className="px-5 py-2"><span className="font-mono-hud rounded px-2 py-0.5 text-xs font-bold" style={thrColor(r.aht, thr[lvl])}>{fmtAht(r.aht)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
};
